<template>
    <div class="main-layout__container">
        <div class="content__header">
            <div class="content-header__title">Комплектации</div>

            <div class="content-header__actions">
                <router-link :to="{ name: 'AddComplectation' }">
                    <UISimpleButton>
                        <template #icon>
                            <img src="@/assets/icons/ui/plus-white.svg" />
                        </template>
                        Добавить
                    </UISimpleButton>
                </router-link>
            </div>

            <div class="content-header__background content-header__background--1-line-filters"></div>
        </div>

        <FilterPanel
            @search="onSearch"
            @reset="onReset"
        >
            <UIInput
                id="code"
                v-model="complectationsFilter.code"
                type="text"
                label="Код"
            />

            <UISelect
                id="brand_id"
                v-model="complectationsFilter.brand_id"
                :options="brandOptions"
                label="Марка"
            />

            <UISelect
                id="model_id"
                v-model="complectationsFilter.model_id"
                :options="modelOptions"
                label="Модель"
                multiple
                searchable
            />
        </FilterPanel>

        <UISearchResults
            :count="complectationPaginationMeta?.pagination.total || 0"
            :is-visible-sort="false"
        />

        <UILoader v-if="isComplectationsLoading" class="tableable" />

        <div v-else class="content__body">
            <DataTable
                :headers="headers"
                :data="arComplectations"
                :sort-key="sortKey"
                :sort-order="sortOrder"
                has-actions
                @sortable="changeSort"
            >
                <template #created_at="{ row }">
                    {{ formatFromUTC(row.created_at) }}
                </template>

                <template #actions="{ row }">
                    <UIDropDown as-modal-on-mobile>
                        <template #elements>
                            <router-link
                                :to="{ name: 'EditComplectation', params: { complectation_id: row.id } }"
                                class="ui-dropdown-menu__element"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/edit.svg" />
                                <span>Редактировать</span>
                            </router-link>

                            <li
                                class="ui-dropdown-menu__element danger"
                                @click="handleDeleteComplectation(row as Complectation)"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/trash.svg" />
                                <span>Удалить</span>
                            </li>
                        </template>
                    </UIDropDown>
                </template>
            </DataTable>
        </div>

        <div class="content__footer">
            <TablePagination
                :total-pages="complectationPaginationMeta?.pagination.last_page ?? 1"
                :current-page="complectationPaginationMeta?.pagination.current_page ?? 1"
                :items-per-page="complectationPaginationMeta?.pagination.per_page ?? 10"
                @page-change="changePage"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { ref, onMounted, inject, h, computed } from 'vue';

    import { ModalPlugin } from '@/plugins/modalPlugin.ts';

    import { SelectOption } from '@/types/form.type.ts';
    import { Complectation, ComplectationsRequestFilter } from '@/types/complectation.type.ts';
    import { Model } from '@/types/model.type.ts';

    import useComplectationRepository from '@/repositories/complectation/useComplectationRepository.ts';
    import useModelRepository from '@/repositories/model/useModelRepository.ts';
    import useNotifications from '@/utils/useNotifications.ts';
    import { useComplectationsFilterStore } from '@/stores/complectations/complectations-filter.ts';

    import { formatFromUTC } from '@/utils/DateTimeFormatter.ts';
    import { getBrandSelectOptions } from '@/utils/SelectOptionFillers.ts';

    import DataTable from '@/components/shared/DataTable/DataTable.vue';
    import FilterPanel from '@/components/shared/FilterPanel/FilterPanel.vue';
    import TablePagination from '@/components/shared/TablePagination/TablePagination.vue';

    import UIDropDown from '@/components/ui/UIDropDown/UIDropDown.vue';
    import UIInput from '@/components/ui/UIInput/UIInput.vue';
    import UILoader from '@/components/ui/UILoader/UILoader.vue';
    import UISearchResults from '@/components/ui/UISearchResults/UISearchResults.vue';
    import UISelect from '@/components/ui/UISelect/UISelect.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';

    import ConfirmActionModal from '@/components/modals/ConfirmActionModal/ConfirmActionModal.vue';

    import ComplectationConfirmDelete from '@/components/complectations/ComplectationConfirmDelete/ComplectationConfirmDelete.vue';

    const $modal = inject('$modal') as typeof ModalPlugin;

    const {
        arComplectations,
        complectationPaginationMeta,
        isComplectationsLoading,
        getComplectations,
        deleteComplectation,
    } = useComplectationRepository();
    const { getAllActiveModels } = useModelRepository();
    const { showSuccess } = useNotifications();
    const complectationsFilterStore = useComplectationsFilterStore();

    const complectationsFilter = ref<ComplectationsRequestFilter>({ ...complectationsFilterStore.$state });
    const brandOptions = ref<SelectOption[]>(getBrandSelectOptions());
    const headers = ref([
        { label: 'Код', key: 'code', sortable: false },
        { label: 'Марка', key: 'brand_name', sortable: false },
        { label: 'Модель', key: 'model_name', sortable: true },
        { label: 'Дата добавления', key: 'created_at', sortable: true },
    ]);

    const arModels = ref<Model[]>([]);

    const sortKey = computed<string | undefined>(() => {
        if (complectationsFilter.value.sort) {
            return Object.keys(complectationsFilter.value.sort[0] ?? {})[0];
        }

        return undefined;
    });

    const sortOrder = computed<string | undefined>(() => {
        if (complectationsFilter.value.sort) {
            return Object.values(complectationsFilter.value.sort[0] ?? {})[0];
        }

        return undefined;
    });

    const modelOptions = computed<SelectOption[]>(() => {
        return arModels.value
            .filter((model: Model) => {
                if (complectationsFilter.value.brand_id === undefined) {
                    return true;
                }

                return complectationsFilter.value.brand_id === model.brand_id;
            })
            .map((model: Model) => ({
                label: model.name,
                value: model.id,
            }));
    });

    function onSearch(): void {
        complectationsFilter.value.page = 1;
        fetchComplectations();
    }

    function onReset(): void {
        complectationsFilter.value = {
            brand_id: undefined,
            model_id: undefined,
            code: '',
            page: 1,
            sort: complectationsFilter.value.sort,
        };

        fetchComplectations();
    }

    function changePage(page: number): void {
        complectationsFilter.value.page = page;
        fetchComplectations();
    }

    function changeSort(key: string, dir: 'asc' | 'desc' | undefined): void {
        complectationsFilter.value.sort = dir === undefined ? [{ model_name: 'asc' }] : [{ [key]: dir }];
        fetchComplectations();
    }

    function handleDeleteComplectation(complectation: Complectation): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    h(ComplectationConfirmDelete, { complectation }),
            },
            props: {
                onConfirm: async () => {
                    await deleteComplectation(complectation.id);

                    showSuccess({
                        title: 'Комплектация удалена',
                    });

                    fetchComplectations();
                },
            },
        });
    }

    function mapComplectationsFilterToRequestFilter(): ComplectationsRequestFilter {
        const requestFilter: ComplectationsRequestFilter = {
            page: complectationsFilter.value.page,
            sort: complectationsFilter.value.sort,
        };

        if (complectationsFilter.value.brand_id !== undefined) {
            requestFilter.brand_id = complectationsFilter.value.brand_id;
        }

        if (complectationsFilter.value.model_id !== undefined) {
            requestFilter.model_id = complectationsFilter.value.model_id;
        }

        if (complectationsFilter.value.code !== undefined && complectationsFilter.value.code.length > 0) {
            requestFilter.code = complectationsFilter.value.code;
        }

        return requestFilter;
    }

    function fetchComplectations(): void {
        complectationsFilterStore.$patch({ ...complectationsFilter.value });
        getComplectations(mapComplectationsFilterToRequestFilter());
    }

    function fetchFilterOptions(): void {
        getAllActiveModels().then((fetchedModels: Model[]) => {
            arModels.value = fetchedModels;
        });
    }

    onMounted(() => {
        fetchComplectations();
        fetchFilterOptions();
    });
</script>
